import React, { useMemo, useState, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import moment from 'moment';
import { ViewMode } from 'gantt-task-react';
import {
    applyOptimisticPinToTasks,
    buildConflictBanner,
    collectGanttTaskMoments,
    buildGanttRangeFromMoments,
    expandGanttRangeForZoom,
    computeTeamTimelineWidthPx,
    buildDateTicks,
    TEAM_TICK_STEP,
    detectTaskTimeOverlaps,
} from '../pages/kanbanBoard/ganttHelpers';
import GanttTaskReactChart from './GanttTaskReactChart';
import TimelinePageChrome from './TimelinePageChrome';
import { regroupMembersByAssignee } from '../utils/mapToGanttTasks';
import '../assets/css/timeline-ui.css';

export const userFullName = (u) =>
    `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || u?.email || `User ${u?.user_id}`;

export const userInitials = (u) => {
    const f = (u?.first_name || '').trim();
    const l = (u?.last_name || '').trim();
    const initials = `${f[0] || ''}${l[0] || ''}`.toUpperCase();
    return initials || (u?.email || '?')[0].toUpperCase();
};

export const summarizeTasks = (tasks = []) => {
    let totalHours = 0;
    let spillCount = 0;
    tasks.forEach((t) => {
        totalHours += Number(t.allocated_hours ?? t.estimated_hours ?? 0) || 0;
        if (Number(t.bandwidth_spill_hours) > 0) spillCount += 1;
    });
    return { taskCount: tasks.length, totalHours, spillCount };
};

/** Shared timeline range used by page toolbars (data unchanged). */
export function useTeamStyleTimeline(tasks = [], zoom = 1, previewMap = null) {
    const { rangeStart, rangeEnd, totalMs } = useMemo(() => {
        if (!tasks.length) {
            const start = moment().startOf('isoWeek');
            const end = start.clone().add(7, 'days');
            return { rangeStart: start, rangeEnd: end, totalMs: end.diff(start) || 1 };
        }
        const dates = collectGanttTaskMoments(tasks, previewMap ? { previewMap } : undefined);
        const { rangeStart: min, rangeEnd: max } = buildGanttRangeFromMoments(dates);
        const expanded = expandGanttRangeForZoom(min, max, zoom);
        return {
            rangeStart: expanded.rangeStart,
            rangeEnd: expanded.rangeEnd,
            totalMs: Math.max(expanded.rangeEnd.diff(expanded.rangeStart), 1),
        };
    }, [tasks, previewMap, zoom]);

    const timelineWidthPx = useMemo(
        () => computeTeamTimelineWidthPx(rangeStart, rangeEnd, zoom),
        [rangeStart, rangeEnd, zoom]
    );

    const dateTicks = useMemo(
        () =>
            buildDateTicks(rangeStart, rangeEnd, {
                timelineWidthPx,
                stepOverride: TEAM_TICK_STEP,
                timeFormat: 'h A',
                labelMinPx: 40,
                minGapPctFloor: 0,
            }),
        [rangeStart, rangeEnd, timelineWidthPx]
    );

    return { rangeStart, rangeEnd, totalMs, timelineWidthPx, dateTicks };
}

function preserveEmptyMembers(original = [], grouped = []) {
    const seen = new Set(grouped.map((m) => Number(m.user?.user_id)));
    const extra = [];
    original.forEach((m) => {
        const uid = Number(m.user?.user_id);
        if (uid && !seen.has(uid)) extra.push({ ...m, tasks: m.tasks || [] });
    });
    return extra.length ? [...grouped, ...extra] : grouped;
}

function downloadCsv(filename, rows) {
    const csv = rows
        .map((r) =>
            r
                .map((cell) => {
                    const v = cell == null ? '' : String(cell);
                    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                })
                .join(',')
        )
        .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Shared chart shell for My Timeline, User Timeline, Project Timeline, and Team Planner.
 * Data fetching stays in the pages; this only maps + renders the timeline.
 */
export default function TeamStyleGanttChart({
    members = [],
    sidePanel = null,
    highlightedCardIds = null,
    previewMap = null,
    onBarClick,
    onDateChange,
    onPersonDrop,
    onTaskListDragStart,
    onTaskListDragEnd,
    dropTarget = null,
    refreshing = false,
    emptyMessage = 'No people with tasks in this view.',
    title = null,
    subtitle = null,
    activeCount = null,
    extraHeader = null,
    deferCommit = true,
    conflictPersonName = null,
    scopeUnassigned = null,
    skipRegroup = false,
    defaultView = ViewMode.Week,
}) {
    const [view, setView] = useState(defaultView);
    const [todayNonce, setTodayNonce] = useState(0);
    const [pendingPins, setPendingPins] = useState([]);
    const [applying, setApplying] = useState(false);
    const [moveFocus, setMoveFocus] = useState(false);

    const { members: groupedMembers, unassigned: regroupedUnassigned } = useMemo(() => {
        if (skipRegroup) {
            return { members, unassigned: [] };
        }
        const result = regroupMembersByAssignee(members);
        return {
            members: preserveEmptyMembers(members, result.members),
            unassigned: result.unassigned,
        };
    }, [members, skipRegroup]);

    const displayMembers = useMemo(() => {
        if (!pendingPins.length) return groupedMembers;
        return groupedMembers.map((m) => {
            const uid = Number(m.user?.user_id);
            let tasks = m.tasks || [];
            pendingPins.forEach((pin) => {
                if (Number(pin.sourceUserId) !== uid) return;
                tasks = applyOptimisticPinToTasks(
                    tasks,
                    pin.sourceTask.id,
                    pin.start instanceof Date ? pin.start.toISOString() : pin.start,
                    uid,
                    pin.isResize ? pin.hours : null,
                    false
                );
            });
            return { ...m, tasks };
        });
    }, [groupedMembers, pendingPins]);

    const allTasks = useMemo(
        () => displayMembers.flatMap((m) => m.tasks || []),
        [displayMembers]
    );

    const conflictBanner = useMemo(() => {
        if (displayMembers.length === 1) {
            return buildConflictBanner(
                conflictPersonName || userFullName(displayMembers[0].user),
                displayMembers[0].tasks || []
            );
        }
        const parts = displayMembers
            .map((m) => buildConflictBanner(userFullName(m.user), m.tasks || []))
            .filter(Boolean);
        if (!parts.length) return null;
        const first = parts[0];
        if (parts.length === 1) return first;
        return {
            ...first,
            text: `${parts.length} people have scheduling conflicts. ${first.text}`,
        };
    }, [displayMembers, conflictPersonName]);

    const overCapacityCount = useMemo(() => {
        return displayMembers.filter((m) => {
            const overlaps = detectTaskTimeOverlaps(m.tasks || []);
            const spill = (m.tasks || []).some((t) => Number(t.bandwidth_spill_hours) > 0);
            return overlaps.size > 0 || spill;
        }).length;
    }, [displayMembers]);

    const pendingWarning = pendingPins.length
        ? `Nothing is saved yet.${
              overCapacityCount
                  ? ` ${overCapacityCount} people are over capacity if you apply. Assignees are notified.`
                  : ' Assignees are notified.'
          }`
        : '';

    const handleQueuePin = useCallback((payload) => {
        setPendingPins((prev) => {
            const key = `${payload.sourceTask.id}:${payload.sourceUserId}`;
            const next = prev.filter(
                (p) => `${p.sourceTask.id}:${p.sourceUserId}` !== key
            );
            next.push(payload);
            return next;
        });
    }, []);

    const handleDiscard = useCallback(() => setPendingPins([]), []);

    const handleApply = useCallback(async () => {
        if (!onDateChange || !pendingPins.length) {
            setPendingPins([]);
            return;
        }
        setApplying(true);
        try {
            for (const pin of pendingPins) {
                const ok = await onDateChange(pin);
                if (ok === false) {
                    setPendingPins([]);
                    return;
                }
            }
            setPendingPins([]);
        } finally {
            setApplying(false);
        }
    }, [onDateChange, pendingPins]);

    const handlePrint = () => window.print();

    const handleExport = () => {
        const rows = [
            ['Person', 'Task', 'Project', 'Start', 'End', 'Due', 'Hours', 'Priority'],
            ...displayMembers.flatMap((m) =>
                (m.tasks || []).map((t) => [
                    userFullName(m.user),
                    t.title,
                    t.project?.project_name || t.board_name || '',
                    t.scheduled_start_at || '',
                    t.scheduled_end_at || '',
                    t.deadline_at || '',
                    t.allocated_hours ?? t.estimated_hours ?? '',
                    t.priority || '',
                ])
            ),
        ];
        downloadCsv(`timeline-${moment().format('YYYY-MM-DD')}.csv`, rows);
    };

    const highlightIds = useMemo(() => {
        if (highlightedCardIds instanceof Set) return highlightedCardIds;
        if (highlightedCardIds != null && highlightedCardIds !== '') {
            return new Set([Number(highlightedCardIds)]);
        }
        if (!moveFocus || !conflictBanner?.overlapMap) return null;
        return new Set([...conflictBanner.overlapMap.keys()].map(Number));
    }, [highlightedCardIds, moveFocus, conflictBanner]);

    const chart = (
        <div className={`team-planner-layout tl-board${refreshing ? ' is-refreshing' : ''}`}>
            {sidePanel}
            <div className="team-planner-timeline gantt-react-timeline">
                <GanttTaskReactChart
                    members={displayMembers}
                    highlightedCardIds={highlightIds}
                    previewMap={previewMap}
                    onBarClick={onBarClick}
                    onDateChange={deferCommit ? undefined : onDateChange}
                    onQueuePin={deferCommit ? handleQueuePin : undefined}
                    deferCommit={deferCommit}
                    onPersonDrop={onPersonDrop}
                    onTaskListDragStart={onTaskListDragStart}
                    onTaskListDragEnd={onTaskListDragEnd}
                    dropUserId={dropTarget?.userId ?? null}
                    emptyMessage={emptyMessage}
                    viewMode={view}
                    todayNonce={todayNonce}
                />
            </div>
        </div>
    );

    if (!title) {
        return chart;
    }

    return (
        <TimelinePageChrome
            title={title}
            activeCount={activeCount ?? allTasks.length}
            subtitle={subtitle}
            extraHeader={extraHeader}
            banner={conflictBanner}
            onShowWhatToMove={() => setMoveFocus(true)}
            view={view}
            onViewChange={setView}
            onToday={() => setTodayNonce((n) => n + 1)}
            refreshing={refreshing}
            onPrint={handlePrint}
            onExport={handleExport}
            pendingCount={pendingPins.length}
            pendingWarning={pendingWarning}
            overCapacityCount={overCapacityCount}
            onDiscard={handleDiscard}
            onApply={handleApply}
            applying={applying}
        >
            {chart}
            {refreshing && (
                <span className="visually-hidden">
                    <Spinner animation="border" size="sm" /> Updating schedule
                </span>
            )}
            {scopeUnassigned}
            {regroupedUnassigned?.length > 0 && !sidePanel ? (
                <p className="text-muted small mt-2 mb-0">
                    {regroupedUnassigned.length} card
                    {regroupedUnassigned.length === 1 ? '' : 's'} could not be placed on a person
                    row (missing assignee).
                </p>
            ) : null}
        </TimelinePageChrome>
    );
}

export { TimelinePageChrome };
